import { useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface ConversionData {
  name: string;
  leads: number;
  conversions: number;
}

interface ReferralLog {
  id: string;
  studentName: string;
  referralCode: string;
  status: "PENDING" | "VERIFIED" | "COMPLETED";
  pointsEarned: number;
  createdAt: string;
}

export default function MarketingConversions() {
  const [data, setData] = useState<ConversionData[]>([]);
  const [referrals, setReferrals] = useState<ReferralLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Generate high-fidelity analytical mock data for conversion trends
    setData([
      { name: "Week 1", leads: 45, conversions: 12 },
      { name: "Week 2", leads: 52, conversions: 18 },
      { name: "Week 3", leads: 85, conversions: 32 },
      { name: "Week 4", leads: 110, conversions: 48 },
      { name: "Week 5", leads: 95, conversions: 42 },
      { name: "Week 6", leads: 140, conversions: 65 },
    ]);

    // Fetch referral logs or simulate live logs
    setReferrals([
      { id: "REF-001", studentName: "Adil Rahman", referralCode: "ADIL99X", status: "COMPLETED", pointsEarned: 500, createdAt: "2026-05-24T10:30:00Z" },
      { id: "REF-002", studentName: "Fathima Sherin", referralCode: "FAT52A", status: "VERIFIED", pointsEarned: 250, createdAt: "2026-05-25T14:15:00Z" },
      { id: "REF-003", studentName: "Midlaj PV", referralCode: "MIDL88P", status: "PENDING", pointsEarned: 0, createdAt: "2026-05-26T09:45:00Z" },
      { id: "REF-004", studentName: "Rinshad K", referralCode: "RIN10K", status: "COMPLETED", pointsEarned: 500, createdAt: "2026-05-26T18:20:00Z" },
    ]);

    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-on-surface">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">Hydrating conversion statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-6 duration-1000 pb-20 text-left">
      {/* Page Title */}
      <div className="flex flex-col space-y-2 px-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-500 bg-emerald-500/10 px-3 py-1 rounded-full">Analytics Hub</span>
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        </div>
        <h2 className="text-4xl lg:text-5xl font-black text-on-surface tracking-tighter leading-tight italic">
          Conversion <span className="text-emerald-500">Analytics.</span>
        </h2>
        <p className="text-sm font-medium text-on-surface-variant/60">Real-time student conversion metrics and referral performance.</p>
      </div>

      {/* Conversion Funnel Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-surface-container-low p-6 rounded-[2rem] border border-outline-variant/10 shadow-sm flex flex-col justify-between h-44 relative overflow-hidden group">
          <span className="material-symbols-outlined text-3xl text-emerald-500 mb-3 group-hover:scale-110 transition-transform">group_add</span>
          <div>
            <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-1">Total Leads</p>
            <h4 className="text-3xl font-black text-on-surface tracking-tighter">527</h4>
            <p className="text-[10px] text-emerald-600 font-bold mt-1">↑ 14.2% this month</p>
          </div>
        </div>
        <div className="bg-surface-container-low p-6 rounded-[2rem] border border-outline-variant/10 shadow-sm flex flex-col justify-between h-44 relative overflow-hidden group">
          <span className="material-symbols-outlined text-3xl text-blue-500 mb-3 group-hover:scale-110 transition-transform">how_to_reg</span>
          <div>
            <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-1">Converted Members</p>
            <h4 className="text-3xl font-black text-on-surface tracking-tighter">217</h4>
            <p className="text-[10px] text-emerald-600 font-bold mt-1">↑ 8.5% conversion rate</p>
          </div>
        </div>
        <div className="bg-surface-container-low p-6 rounded-[2rem] border border-outline-variant/10 shadow-sm flex flex-col justify-between h-44 relative overflow-hidden group">
          <span className="material-symbols-outlined text-3xl text-purple-500 mb-3 group-hover:scale-110 transition-transform">payments</span>
          <div>
            <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-1">Referral Yield</p>
            <h4 className="text-3xl font-black text-on-surface tracking-tighter">1,250</h4>
            <p className="text-[10px] text-purple-500 font-bold mt-1">Points assigned</p>
          </div>
        </div>
        <div className="bg-surface-container-low p-6 rounded-[2rem] border border-outline-variant/10 shadow-sm flex flex-col justify-between h-44 relative overflow-hidden group">
          <span className="material-symbols-outlined text-3xl text-orange-500 mb-3 group-hover:scale-110 transition-transform">conversion_path</span>
          <div>
            <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-1">Avg Ticket Value</p>
            <h4 className="text-3xl font-black text-on-surface tracking-tighter">41.2%</h4>
            <p className="text-[10px] text-slate-400 font-bold mt-1">Lead-to-Active Ratio</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Flow Chart */}
        <div className="lg:col-span-8 bg-surface-container-low p-8 rounded-[3.5rem] border border-outline-variant/10 shadow-sm flex flex-col gap-6">
          <div>
            <h3 className="text-xl font-black text-on-surface tracking-tighter">Conversion Funnel Velocity</h3>
            <p className="text-[10px] font-bold text-on-surface-variant/40 uppercase tracking-widest mt-0.5">Leads vs Conversions Trend</p>
          </div>
          <div className="h-[300px] w-full mt-4 -ml-6">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorConversions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" opacity={0.4} />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} axisLine={false} tickLine={false} dy={10} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} axisLine={false} tickLine={false} dx={-10} />
                <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: 'none', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }} />
                <Area type="monotone" dataKey="leads" name="Leads" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorLeads)" />
                <Area type="monotone" dataKey="conversions" name="Conversions" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorConversions)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Column: Live Referral Logs */}
        <div className="lg:col-span-4 bg-surface-container-low p-8 rounded-[3.5rem] border border-outline-variant/10 shadow-sm relative overflow-hidden">
          <h3 className="text-xl font-black text-on-surface tracking-tighter mb-6">Referral Logs</h3>
          <div className="space-y-4">
            {referrals.map((ref) => (
              <div key={ref.id} className="p-4 bg-surface-container-highest/20 rounded-2xl flex flex-col gap-2">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{ref.id}</span>
                  <span className={`text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wider ${
                    ref.status === "COMPLETED" ? "bg-emerald-500/10 text-emerald-500" :
                    ref.status === "VERIFIED" ? "bg-blue-500/10 text-blue-500" :
                    "bg-amber-500/10 text-amber-500"
                  }`}>{ref.status}</span>
                </div>
                <div className="flex justify-between items-end">
                  <div>
                    <h4 className="text-xs font-black text-on-surface">{ref.studentName}</h4>
                    <p className="text-[9px] font-bold text-on-surface-variant/40 uppercase tracking-tight mt-0.5">Code: {ref.referralCode}</p>
                  </div>
                  {ref.pointsEarned > 0 && (
                    <span className="text-xs font-black text-emerald-600">+{ref.pointsEarned} pts</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
